import { TRANSACTION_ITEMS } from "../../../pages/buyer/BuyerTransactionChecklist";
import { TIPS as BUYER_TIPS } from "../../../pages/buyer/BuyerTips";
import { TIPS as SELLER_TIPS } from "../../../pages/seller/SellerTips";
import { peekBuyers } from "../buyersApi";
import { peekEvents } from "../calendarApi";
import { listClientDocuments } from "../../db/documents";
import { formatDate, formatNotesText, preferenceProgress, prettyValue, summarizeChecklist } from "../../utils/formatters";
import { sellerProgress, summarizeSellerPortfolio } from "../../utils/sellerSteps";
import {
  FILE_STATUSES,
  LIFECYCLE_STAGES,
  SELLER_TRANSACTION_ITEMS,
  fileStatusLabel,
  stepStatusLabel,
} from "./constants";
import { buyerFileId, fileRouteId, parseFileRouteId, sellerFileId } from "./ids";
import { getOverlay } from "./store";

function peekSellers() {
  try {
    return JSON.parse(localStorage.getItem("dh_sellers") || "[]");
  } catch {
    return [];
  }
}

function formatStamp(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${formatDate(iso)} · ${date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function buyerAddress(preferences = {}) {
  const loc = preferences.location || {};
  return [loc.city, loc.county, loc.state, loc.region].filter(Boolean).join(", ");
}

function sellerAddress(portfolio = {}, fallback = "") {
  const details = portfolio.propertyDetails || {};
  return [details.street, details.city, details.state, details.zip].filter(Boolean).join(", ") || fallback || "";
}

function matchBuyer(buyers, clientId, email) {
  return (
    buyers.find((item) => item.id === clientId) ||
    buyers.find((item) => email && item.email && item.email.toLowerCase() === email.toLowerCase()) ||
    null
  );
}

function matchSeller(sellers, clientId, email) {
  return (
    sellers.find((item) => item.id === clientId) ||
    sellers.find((item) => email && item.email && item.email.toLowerCase() === email.toLowerCase()) ||
    null
  );
}

function matchTransaction(transactions, clientId, email, role) {
  return (
    transactions.find((item) => item.client_id === clientId && (!role || item.client_role === role)) ||
    transactions.find(
      (item) =>
        email &&
        item.client?.email &&
        item.client.email.toLowerCase() === email.toLowerCase() &&
        (!role || item.client_role === role)
    ) ||
    null
  );
}

function deriveFileStatus(transaction, overlay) {
  if (overlay.status && overlay.status !== "active") return overlay.status;
  if (transaction?.status === "closed") return "closed";
  if (transaction?.status === "abandoned") return "dissolved";
  return overlay.status || transaction?.status || "active";
}

function eventsForClient(events, { clientId, name, address }) {
  const nameLc = String(name || "").toLowerCase();
  const addressLc = String(address || "").toLowerCase();
  return events.filter((item) => {
    if (clientId && item.buyerId && item.buyerId === clientId) return true;
    if (nameLc && String(item.contactName || "").toLowerCase() === nameLc) return true;
    if (addressLc && String(item.location || "").toLowerCase().includes(addressLc.split(",")[0])) return true;
    if (nameLc && String(item.title || "").toLowerCase().includes(nameLc)) return true;
    return false;
  });
}

function buyerMilestones(preferences = {}) {
  const saved = preferences.transactionChecklist || {};
  const statuses = saved.statuses || {};
  return TRANSACTION_ITEMS.map((item) => ({
    ...item,
    status: statuses[item.id] || "",
    source: "buyer",
  }));
}

function sellerMilestones(overlay) {
  const statuses = overlay.sellerMilestones?.statuses || {};
  return SELLER_TRANSACTION_ITEMS.map((item) => ({
    ...item,
    status: statuses[item.id] || "not_started",
    source: "seller",
  }));
}

function lifecycleRows(overlay) {
  return LIFECYCLE_STAGES.map((stage) => {
    const row = overlay.lifecycle?.[stage.id] || { status: "not_started", notes: "" };
    return {
      ...stage,
      status: row.status || "not_started",
      notes: row.notes || "",
      updatedAt: row.updatedAt || "",
    };
  });
}

function pageNotes(fileType, preferences, portfolio) {
  const notes = fileType === "seller" ? portfolio?.pageNotes || {} : preferences?.pageNotes || {};
  return Object.entries(notes)
    .filter(([, text]) => String(text || "").trim())
    .map(([page, text]) => ({ page: prettyValue(page), text: String(text) }));
}

function tipsSummary(fileType, preferences, portfolio) {
  if (fileType === "seller") {
    const tips = portfolio?.tips || {};
    return SELLER_TIPS.map((item) => ({
      title: item.title,
      status: tips[item.id] || tips.acknowledged ? "Acknowledged" : "Unread",
    }));
  }
  return BUYER_TIPS.map((item) => ({
    title: item.title,
    status: preferences?.tipsAcknowledged ? "Acknowledged" : "Unread",
  }));
}

function messageTimeline(transaction) {
  const parties = [transaction?.broker, transaction?.agent, transaction?.client];
  return (transaction?.threads || [])
    .flatMap((thread) =>
      (thread.messages || []).map((message) => {
        const sender = parties.find((person) => person?.id === message.sender_id);
        return {
          at: message.timestamp,
          stamp: formatStamp(message.timestamp),
          source: "message",
          title: sender?.name || "Someone",
          text: message.message_text || "",
          attachments: message.attachments || [],
        };
      })
    )
    .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
}

function buildActionTimeline({ transaction, overlay, events, documents, fileType }) {
  const items = [];

  if (transaction?.created_at) {
    items.push({
      at: transaction.created_at,
      stamp: formatStamp(transaction.created_at),
      source: "file",
      title: "File opened",
      text: transaction.property_address || "Conversation started",
    });
  }

  messageTimeline(transaction).forEach((item) => items.push(item));

  events.forEach((event) => {
    const at = event.date ? `${event.date}T${event.time || "00:00"}` : "";
    items.push({
      at,
      stamp: [event.date, event.time].filter(Boolean).join(" · "),
      source: "calendar",
      title: event.title || "Appointment",
      text: [prettyValue(event.type), event.location, event.notes].filter(Boolean).join(" · "),
    });
  });

  documents.forEach((doc) => {
    items.push({
      at: doc.created_at,
      stamp: formatStamp(doc.created_at),
      source: "attachment",
      title: doc.file_name || "Document",
      text: doc.mime_type || "Uploaded to this file",
    });
  });

  (overlay.customSteps || []).forEach((step) => {
    items.push({
      at: step.date || step.updatedAt || step.createdAt,
      stamp: formatStamp(step.date || step.updatedAt || step.createdAt),
      source: "custom",
      title: step.title || "Custom step",
      text: [stepStatusLabel(step.status), step.notes || step.description].filter(Boolean).join(" · "),
    });
  });

  LIFECYCLE_STAGES.forEach((stage) => {
    const row = overlay.lifecycle?.[stage.id];
    if (!row?.updatedAt) return;
    items.push({
      at: row.updatedAt,
      stamp: formatStamp(row.updatedAt),
      source: "lifecycle",
      title: stage.title,
      text: [stepStatusLabel(row.status), row.notes].filter(Boolean).join(" · "),
    });
  });

  if (transaction?.closed_at) {
    items.push({
      at: transaction.closed_at,
      stamp: formatStamp(transaction.closed_at),
      source: "file",
      title: transaction.status === "abandoned" ? "File dissolved" : "File closed",
      text: "Moved to the archive after a communication report.",
    });
  }

  if (overlay.pdfPacket?.generatedAt) {
    items.push({
      at: overlay.pdfPacket.generatedAt,
      stamp: formatStamp(overlay.pdfPacket.generatedAt),
      source: "pdf",
      title: "File packet PDF",
      text: overlay.pdfPacket.filename || "Generated on demand",
    });
  }

  return items.sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
}

function collectAttachments({ transaction, documents, overlay, seller, buyer }) {
  const messageFiles = (transaction?.threads || []).flatMap((thread) =>
    (thread.messages || []).flatMap((message) =>
      (message.attachments || []).map((file) => ({
        id: file.id,
        name: file.file_name || "Attachment",
        type: file.mime_type || "",
        stamp: formatStamp(file.uploaded_at),
        source: "message",
      }))
    )
  );
  const docs = documents.map((doc) => ({
    id: doc.id,
    name: doc.file_name,
    type: doc.mime_type,
    stamp: formatStamp(doc.created_at),
    source: "document",
  }));
  const custom = (overlay.customSteps || []).flatMap((step) =>
    (step.attachments || []).map((file) => ({
      id: file.id,
      name: file.file_name || file.name || "Attachment",
      type: file.mime_type || "",
      stamp: formatStamp(file.added_at || step.date),
      source: "custom",
      stepTitle: step.title,
    }))
  );
  const office = (overlay.attachments || []).map((file) => ({
    id: file.id,
    name: file.file_name || file.name || "Attachment",
    type: file.mime_type || "",
    stamp: formatStamp(file.added_at),
    source: "file",
  }));
  const photos = (seller?.portfolio?.photos?.items || []).map((item) => ({
    id: item.id,
    name: item.fileName || item.caption || "Seller photo",
    type: item.mime_type || "image/*",
    stamp: formatStamp(item.addedAt),
    source: "photo",
  }));
  const buyerPhotos = (buyer?.preferences?.notes?.attachments || []).map((item) => ({
    id: item.id,
    name: item.file_name || item.fileName || "Buyer file",
    type: item.mime_type || "",
    stamp: formatStamp(item.created_at || item.addedAt),
    source: "photo",
  }));
  return [...messageFiles, ...docs, ...custom, ...office, ...photos, ...buyerPhotos];
}

export function assembleFileFromParts({ transaction = null, buyer = null, seller = null, events = [], overlayId }) {
  const fileType = transaction?.client_role || (seller && !buyer ? "seller" : "buyer");
  const clientRecord = fileType === "seller" ? seller : buyer;
  const client = transaction?.client || null;
  const preferences = buyer?.preferences || {};
  const portfolio = seller?.portfolio || {};
  const clientId = client?.id || clientRecord?.id || transaction?.client_id || "";
  const fileId =
    overlayId ||
    fileRouteId({
      transactionId: transaction?.id,
      fileType,
      clientId,
    });
  const overlay = getOverlay(fileId);
  const address =
    transaction?.property_address ||
    (fileType === "seller" ? sellerAddress(portfolio) : buyerAddress(preferences));
  const documents = listClientDocuments(clientId, fileType);
  const calendarEvents = eventsForClient(events, {
    clientId,
    name: client?.name || clientRecord?.name,
    address,
  });
  const status = deriveFileStatus(transaction, overlay);
  const checklistSections =
    fileType === "seller" ? summarizeSellerPortfolio(portfolio) : summarizeChecklist(preferences);
  const progress =
    fileType === "seller" ? sellerProgress(portfolio) : preferenceProgress(preferences);
  const transactionTimeline = fileType === "seller" ? sellerMilestones(overlay) : buyerMilestones(preferences);

  const clientProfile = {
    id: clientId,
    name: client?.name || clientRecord?.name || "",
    email: client?.email || clientRecord?.email || "",
    phone: client?.phone || clientRecord?.phone || "",
    address,
    agent: transaction?.agent?.name || seller?.agentName || "",
    agentId: transaction?.agent_id || buyer?.agentId || seller?.agentId || "",
    broker: transaction?.broker?.name || seller?.brokerName || "",
    notes:
      overlay.profileNotes ||
      (fileType === "seller"
        ? [portfolio.notes?.forAgent, portfolio.notes?.showing, portfolio.notes?.disclosures]
            .filter(Boolean)
            .join("\n\n")
        : formatNotesText(preferences.notes)),
  };

  const messages = messageTimeline(transaction);
  const attachments = collectAttachments({ transaction, documents, overlay, seller, buyer });
  const timeline = buildActionTimeline({
    transaction,
    overlay,
    events: calendarEvents,
    documents,
    fileType,
  });

  return {
    id: fileId,
    transactionId: transaction?.id || null,
    fileType,
    status,
    statusLabel: fileStatusLabel(status) || prettyValue(status),
    transactionStatus: transaction?.status || "",
    clientProfile,
    buyerChecklist: fileType === "buyer" ? { preferences, sections: checklistSections, progress } : null,
    sellerChecklist: fileType === "seller" ? { portfolio, sections: checklistSections, progress } : null,
    transactionTimeline,
    sellerMilestoneNotes: overlay.sellerMilestones?.notes || "",
    customSteps: overlay.customSteps || [],
    lifecycle: lifecycleRows(overlay),
    messages,
    threads: transaction?.threads || [],
    calendarEvents,
    attachments,
    documents,
    timeline,
    archives: transaction?.archives || [],
    exports: transaction?.exports || [],
    pdfPacket: overlay.pdfPacket,
    overlay,
    transaction,
    buyer,
    seller,
    createdAt: transaction?.created_at || buyer?.createdAt || seller?.createdAt || "",
    updatedAt: overlay.updatedAt || buyer?.updatedAt || "",
    propertyAddress: address,
    progress,
    checklistSections,
    pageNotes: pageNotes(fileType, preferences, portfolio),
    tips: tipsSummary(fileType, preferences, portfolio),
    specialNotes:
      fileType === "seller"
        ? [portfolio.notes?.forAgent, portfolio.notes?.showing, portfolio.notes?.disclosures]
            .filter(Boolean)
            .join("\n\n")
        : formatNotesText(preferences.notes),
  };
}

export function toIndexRow(file) {
  return {
    id: file.id,
    transactionId: file.transactionId,
    fileType: file.fileType,
    name: file.clientProfile.name,
    email: file.clientProfile.email,
    phone: file.clientProfile.phone,
    address: file.clientProfile.address || file.propertyAddress,
    status: file.status,
    statusLabel: file.statusLabel,
    agent: file.clientProfile.agent,
    agentId: file.clientProfile.agentId,
    when: file.updatedAt || file.createdAt,
    progress: file.progress?.percent || 0,
    customStepCount: file.customSteps.length,
    messageCount: file.messages.length,
    eventCount: file.calendarEvents.length,
    attachmentCount: file.attachments.length,
    clientId: file.clientProfile.id,
  };
}

export function listFileIndex({ transactions = [], buyers = [], sellers = peekSellers(), events = [] }) {
  const rows = [];
  const claimed = new Set();

  transactions.forEach((transaction) => {
    const buyer =
      transaction.client_role === "seller"
        ? null
        : matchBuyer(buyers, transaction.client_id, transaction.client?.email);
    const seller =
      transaction.client_role === "seller"
        ? matchSeller(sellers, transaction.client_id, transaction.client?.email)
        : null;
    const file = assembleFileFromParts({ transaction, buyer, seller, events, overlayId: transaction.id });
    rows.push(toIndexRow(file));
    if (file.clientProfile.id) claimed.add(`${file.fileType}:${file.clientProfile.id}`);
    if (file.clientProfile.email) claimed.add(`${file.fileType}:${file.clientProfile.email.toLowerCase()}`);
  });

  buyers.forEach((buyer) => {
    const keyId = `buyer:${buyer.id}`;
    const keyEmail = buyer.email ? `buyer:${buyer.email.toLowerCase()}` : "";
    if (claimed.has(keyId) || (keyEmail && claimed.has(keyEmail))) return;
    if (matchTransaction(transactions, buyer.id, buyer.email, "buyer")) return;
    const file = assembleFileFromParts({
      buyer,
      events,
      overlayId: buyerFileId(buyer.id),
    });
    rows.push(toIndexRow(file));
  });

  sellers.forEach((seller) => {
    if (!seller.id) return;
    const keyId = `seller:${seller.id}`;
    const keyEmail = seller.email ? `seller:${seller.email.toLowerCase()}` : "";
    if (claimed.has(keyId) || (keyEmail && claimed.has(keyEmail))) return;
    if (matchTransaction(transactions, seller.id, seller.email, "seller")) return;
    const file = assembleFileFromParts({
      seller,
      events,
      overlayId: sellerFileId(seller.id),
    });
    rows.push(toIndexRow(file));
  });

  return rows.sort((a, b) => String(b.when || "").localeCompare(String(a.when || "")));
}

export function loadFile(routeId, { transactions = [], buyers = peekBuyers(), sellers = peekSellers(), events = peekEvents() } = {}) {
  const parsed = parseFileRouteId(routeId);
  if (parsed.kind === "transaction") {
    const transaction = transactions.find((item) => item.id === parsed.transactionId) || null;
    if (!transaction) return null;
    const buyer =
      transaction.client_role === "seller"
        ? null
        : matchBuyer(buyers, transaction.client_id, transaction.client?.email);
    const seller =
      transaction.client_role === "seller"
        ? matchSeller(sellers, transaction.client_id, transaction.client?.email)
        : null;
    return assembleFileFromParts({ transaction, buyer, seller, events, overlayId: transaction.id });
  }
  if (parsed.kind === "buyer") {
    const buyer = matchBuyer(buyers, parsed.clientId, "");
    if (!buyer) return null;
    const transaction = matchTransaction(transactions, buyer.id, buyer.email, "buyer");
    if (transaction) {
      return assembleFileFromParts({ transaction, buyer, events, overlayId: transaction.id });
    }
    return assembleFileFromParts({ buyer, events, overlayId: parsed.fileId });
  }
  const seller = matchSeller(sellers, parsed.clientId, "");
  if (!seller) return null;
  const transaction = matchTransaction(transactions, seller.id, seller.email, "seller");
  if (transaction) {
    return assembleFileFromParts({ transaction, seller, events, overlayId: transaction.id });
  }
  return assembleFileFromParts({ seller, events, overlayId: parsed.fileId });
}

export function resolveClientFileId({ transactions, buyer, seller }) {
  if (buyer) {
    const tx = matchTransaction(transactions, buyer.id, buyer.email, "buyer");
    return fileRouteId({ transactionId: tx?.id, fileType: "buyer", clientId: buyer.id });
  }
  if (seller) {
    const tx = matchTransaction(transactions, seller.id, seller.email, "seller");
    return fileRouteId({ transactionId: tx?.id, fileType: "seller", clientId: seller.id });
  }
  return "";
}

export { peekSellers, FILE_STATUSES };
